import { useMemo } from 'react';

import { useCollections } from '../contexts/CollectionsContext';
import { useOptionalFavorites } from '../contexts/FavoritesContext';
import { isTokenValid } from '../utils';
import useRecentCollections from './useRecentCollections';
import { normalizeId } from '../utils/ids';

const RECENT_LIMIT = 5;
const NO_ROWS = Object.freeze([]);

/**
 * What the crumb's collection switcher lists (docs/platform-standards.md §13),
 * from the SAME sources the rail, Home and the command palette read, so they
 * never disagree:
 *
 *   all        the shared collections list (contexts/CollectionsContext, GET
 *              /api/collections): every collection the person can open, so
 *              nothing here can lead to a 404
 *   recent     the server's collection history, newest first (useRecentCollections:
 *              GET /api/views/recent?targetType=collection, what the rail's Recent
 *              section and the palette's recents are)
 *   favorites  the person's starred collections in their own order (FavoritesContext)
 *
 * Recent and favorites are narrowed to `all` once it has answered: a history row
 * for a collection that was since un-shared stays out. Mount this only while the
 * switcher is open, so a page that never opens it asks for nothing.
 */
export default function useCollectionSwitcherSource() {
	const favorites = useOptionalFavorites();
	const { rows: recentRows } = useRecentCollections({ limit: RECENT_LIMIT });
	const { collections, available, error, refresh } = useCollections();
	// The list failed: `all` stays null (a failure is not an empty list) and the
	// switcher says so with a Retry.
	const failed = !available && Boolean(error);
	const all = available ? collections : (isTokenValid() ? null : NO_ROWS);
	const retry = refresh;

	const byId = useMemo(() => {
		const map = new Map();
		(all || []).forEach(collection => {
			const id = normalizeId(collection?._id);
			if (id) map.set(id, collection);
		});
		return map;
	}, [all]);

	const favoriteIds = favorites?.favoriteCollectionOrder;
	const favoriteSet = favorites?.favoriteCollectionIds;

	return useMemo(() => {
		// Until the list answers, a row's own record stands in (it is what the
		// server just said about it); once it has, only listed collections stay.
		const resolve = (id, fallback) => (all === null ? fallback : byId.get(id)) || null;
		const recent = recentRows
			.map(row => resolve(row.id, row.collection))
			.filter(Boolean);
		const ids = favoriteIds || [...(favoriteSet || [])];
		const starred = ids.map(id => byId.get(normalizeId(id))).filter(Boolean);
		return { all: all || [], recent, favorites: starred, loading: all === null && !failed, error: all === null && failed, retry };
	}, [all, failed, retry, byId, recentRows, favoriteIds, favoriteSet]);
}
